import { useState, useEffect, useRef, memo } from 'react';
import { Play } from 'lucide-react';

interface VideoThumbnailProps {
  video: {
    video_url: string;
    thumbnail_url: string | null;
    title: string;
  };
  index?: number;
  priority?: boolean; // Load immediately for above-fold content
}

export const VideoThumbnail = memo(({ video, index = 0, priority = false }: VideoThumbnailProps) => {
  const [isVisible, setIsVisible] = useState(priority);
  const [isLoaded, setIsLoaded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (priority) return; // Skip observer for priority items
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Stagger loading by 50ms per video to prevent network flooding
          setTimeout(() => {
            setIsVisible(true);
          }, index * 50);
          observer.disconnect();
        }
      },
      { 
        rootMargin: '100px', // Reduced from 200px for faster initial load
        threshold: 0.01
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [index, priority]);

  // If thumbnail exists, show it with optimized loading
  if (video.thumbnail_url) {
    return (
      <img
        src={video.thumbnail_url}
        alt={video.title}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        className="w-full h-full object-cover group-hover:brightness-110 transition-all duration-200"
      />
    );
  }

  // Otherwise, use smart lazy loading with video
  return (
    <div ref={ref} className="w-full h-full relative">
      {/* Instant gradient placeholder - always visible */}
      <div className="absolute inset-0 bg-gradient-to-br from-neon-purple/30 via-neon-pink/20 to-neon-cyan/30 flex items-center justify-center">
        <Play className="w-12 h-12 text-white/40" />
      </div>
      
      {/* Load video only when visible */}
      {isVisible && (
        <video
          src={video.video_url}
          preload="metadata"
          muted
          playsInline
          onLoadedData={() => setIsLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}
    </div>
  );
});

VideoThumbnail.displayName = 'VideoThumbnail';

